import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { AlternativePeriodSuggestions } from "@/components/AlternativePeriodSuggestions";
import { AnimatedAlert } from "@/components/AnimatedAlert";
import { AvailabilityCheckingScreen } from "@/components/AvailabilityCheckingScreen";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { BookingStepTransition } from "@/components/BookingStepTransition";
import { BookingTermsAcceptance } from "@/components/BookingTermsAcceptance";
import { BookingSuccessScreen } from "@/components/BookingSuccessScreen";
import { GuestAuthPrompt } from "@/components/GuestAuthPrompt";
import { BookingPeriodDateFields } from "@/components/BookingPeriodDateFields";
import { formatLicensePlateForDisplay } from "@/lib/licensePlate";
import { NoAvailabilityNotice } from "@/components/NoAvailabilityNotice";
import { NoVehiclePitchNotice } from "@/components/NoVehiclePitchNotice";
import { SoldOutPeriodNotice } from "@/components/SoldOutPeriodNotice";
import { PeriodSelectedHint } from "@/components/PeriodSelectedHint";
import { BookingHoldTimer } from "@/components/BookingHoldTimer";
import { ArrivalTypeSelection } from "@/components/ArrivalTypeSelection";
import { ElectricitySelection } from "@/components/ElectricitySelection";
import { BookingStepIndicator } from "@/components/BookingStepIndicator";
import { CategorySelectionCards } from "@/components/CategorySelectionCards";
import { CompanionFieldsForm } from "@/components/CompanionFieldsForm";
import { Notice } from "@/components/Notice";
import { OptionTileGroup } from "@/components/OptionTileGroup";
import { PrimaryGuestFieldsForm } from "@/components/PrimaryGuestFieldsForm";
import { PartyCountField } from "@/components/PartyCountField";
import { StepHeader } from "@/components/StepHeader";
import { StayContextBar, type StayContextItem } from "@/components/StayContextBar";
import { SummaryRows, SummarySection } from "@/components/SummarySection";
import { VehicleDetailsFields } from "@/components/VehicleDetailsFields";
import {
  BoltIcon,
  CalendarIcon,
  InfoIcon,
  MessageIcon,
  MoonIcon,
  PinIcon,
  UsersIcon,
  VehicleTypeIcon,
} from "@/components/ui/Icons";
import { formatCurrency } from "@/lib/money";
import {
  applyBirthDatePartyAdjustments,
  birthDateAgeValidationActive,
  getPrimaryGuestBirthDateError,
  syncCompanionsToPartyCounts,
  validateTravelerBirthDates,
} from "@/lib/birthDateAge";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { Spinner } from "@/components/ui/Spinner";
import { useLocale } from "@/i18n";
import type { StepDirection } from "@/lib/motion";
import { bookingLegalContextFromConfig } from "@/lib/bookingLegalTexts";
import { api, ApiError } from "@/lib/api";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";
import { companionSlotLabel } from "@/lib/companionFields";
import {
  buildCompanionsPayload,
  DEFAULT_COMPANION_FIELDS,
  emptyCompanion,
  expectedCompanionRoles,
  normalizeGuestAddressFieldModes,
  resolveCompanionFieldConfigs,
  validateCompanions,
  type CompanionFormData,
} from "@/lib/companionFields";
import { loadGuestSession, saveGuestSession, type GuestProfile } from "@/lib/guestAuth";
import {
  collectUnfilledMandatoryFields,
  companionSummaryLines,
  formatCompanionFieldValue,
} from "@/lib/guestProfile";
import {
  buildPrimaryGuestPayload,
  DEFAULT_PRIMARY_GUEST_FIELDS,
  emptyPrimaryGuestForm,
  getMissingPrimaryGuestFields,
  primaryGuestSummaryLines,
  validatePrimaryGuest,
  type PrimaryGuestFormData,
} from "@/lib/primaryGuestFields";
import {
  activeVehicleFields,
  buildVehiclePayload,
  EMPTY_VEHICLE_FORM,
  vehicleFieldFullConfigFor,
  vehicleFormReady,
  vehicleLengthMFromForm,
  vehicleFilterParamsFromForm,
  vehicleDimensionsAffectAvailability,
  appendVehicleAvailabilityParams,
  vehicleSummaryLines,
  validateVehicleForm,
  type VehicleFormData,
} from "@/lib/vehicleFields";
import type { KnownCompanion } from "@/lib/knownCompanions";
import { isBookingPeriodInSeasons, isDateBookable } from "@/lib/bookingSeasons";
import {
  hasSoldOutPeriodBlock,
  isArrivalBlockedBySoldOut,
  isDepartureBlockedBySoldOut,
  resolveBackendPeriodBlockMessage,
  soldOutNightsSet,
} from "@/lib/soldOut";
import {
  addDays,
  formatLocaleDate,
  nightsBetween,
  todayLocal,
  shouldShowPeriodValidationError,
  validatePeriod,
} from "@/lib/dates";
import {
  earliestBookableArrivalDate,
  isSameDayArrivalBlockedToday,
} from "@/lib/sameDayCutoff";
import type {
  AlternativePeriod,
  AlternativePeriodsResponse,
  BookingResult,
  BookingHold,
  CategoriesResponse,
  CategoryPricesResponse,
  CompanionFieldsConfig,
  CompanionFieldsConfigByRole,
  OnlineBookingFlowSettings,
  PriceQuote,
  PublicBookingConfig,
  PublicCategory,
  UpsellingDisplaySettings,
} from "@/types";
import { DEFAULT_UPSELLING_DISPLAY } from "@/types";

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

function normalizeCategoryId(id: string | null | undefined): string | null {
  if (id == null) return null;
  const trimmed = id.trim();
  return trimmed || null;
}

function categoryIdKey(id: string | null | undefined): string {
  return normalizeCategoryId(id)?.toLowerCase() ?? "";
}

function pitchCategoryIdForApi(categoryId: string | null): string | null {
  return normalizeCategoryId(categoryId);
}

function categoriesIncludeId(categories: PublicCategory[], id: string | null): boolean {
  const key = categoryIdKey(id);
  if (!key) return false;
  return categories.some((cat) => categoryIdKey(cat.pitch_category_id) === key);
}

function resolveCategorySelectionAfterFetch(
  categories: PublicCategory[],
  current: string | null,
  suggestedCategoryId: string | null | undefined,
  preserveManualSelection: boolean,
): string | null {
  if (preserveManualSelection && current && categoriesIncludeId(categories, current)) {
    return normalizeCategoryId(current);
  }

  if (categories.length === 1) {
    return normalizeCategoryId(categories[0].pitch_category_id);
  }

  const suggested = normalizeCategoryId(suggestedCategoryId);
  if (suggested && categoriesIncludeId(categories, suggested)) {
    if (current === null || !categoriesIncludeId(categories, current)) {
      return suggested;
    }
  }

  if (current && !categoriesIncludeId(categories, current)) {
    return null;
  }

  return normalizeCategoryId(current);
}

function mergeCategoryPrices(
  base: CategoriesResponse,
  prices: CategoryPricesResponse,
): CategoriesResponse {
  const priceById = new Map(
    prices.categories.map((entry) => [categoryIdKey(entry.pitch_category_id), entry]),
  );
  const merged = base.categories.map((cat) => {
    const price = priceById.get(categoryIdKey(cat.pitch_category_id));
    if (!price) return cat;
    return {
      ...cat,
      nightly_rate: price.price_pending ? null : (price.nightly_rate ?? cat.nightly_rate),
      rental_period: price.rental_period ?? cat.rental_period,
      total_amount: price.price_pending ? null : (price.total_amount ?? cat.total_amount),
      price_pending: Boolean(price.price_pending),
      nights: price.nights ?? cat.nights,
      price_comparison_text: price.price_comparison_text ?? cat.price_comparison_text,
    };
  });
  if (!prices.categories.length) {
    return { ...base, categories: merged };
  }
  const ordered = prices.categories
    .map((entry) =>
      merged.find(
        (cat) => categoryIdKey(cat.pitch_category_id) === categoryIdKey(entry.pitch_category_id),
      ),
    )
    .filter((cat): cat is NonNullable<typeof cat> => Boolean(cat));
  return { ...base, categories: ordered.length ? ordered : merged };
}

function arrivalTypeLabel(value: string, options: { value: string; label: string }[]): string {
  return options.find((t) => t.value === value)?.label ?? value;
}

function buildCategoriesQueryParams(options: {
  arrival: string;
  departure: string;
  wantsElectricity: boolean;
  arrivalType?: string;
  vehicleFilterParams?: ReturnType<typeof vehicleFilterParamsFromForm>;
  holdToken?: string | null;
  locale?: "de" | "en";
  includePrices?: boolean;
}): URLSearchParams {
  const params = new URLSearchParams({
    arrival: options.arrival,
    departure: options.departure,
    wants_electricity: String(options.wantsElectricity),
    locale: options.locale ?? "de",
  });
  appendVehicleAvailabilityParams(
    params,
    options.vehicleFilterParams ?? {},
    options.arrivalType,
  );
  if (options.holdToken) {
    params.set("hold_token", options.holdToken);
  }
  if (options.includePrices) {
    params.set("include_prices", "true");
  }
  return params;
}

export function BookingFlowPage() {
  const { locale, messages, localePath } = useLocale();
  const { siteName: fallbackSiteName, logoUrl: fallbackLogoUrl } = useCampsiteBranding();
  const [step, setStep] = useState<Step>(1);
  const [stepDirection, setStepDirection] = useState<StepDirection>("forward");

  function navigateToStep(target: Step) {
    setStepDirection(target >= step ? "forward" : "back");
    setStep(target);
  }

  const flowSteps = useMemo(
    () => [
      { id: 1 as Step, label: messages.steps.period },
      { id: 2 as Step, label: messages.steps.vehicle },
      { id: 3 as Step, label: messages.steps.pitch },
      { id: 4 as Step, label: messages.steps.details },
      { id: 5 as Step, label: messages.steps.contact },
      { id: 6 as Step, label: messages.steps.summary },
    ],
    [messages.steps],
  );

  useEffect(() => {
    document.title = messages.meta.title;
  }, [messages.meta.title]);

  useEffect(() => {
    setHighlightMissingFields(false);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  const [arrival, setArrival] = useState("");
  const [departure, setDeparture] = useState("");
  const [wantsElectricity, setWantsElectricity] = useState(true);
  const wantsElectricityInitializedRef = useRef(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categories, setCategories] = useState<CategoriesResponse | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [alternatives, setAlternatives] = useState<AlternativePeriodsResponse | null>(null);
  const [alternativesLoading, setAlternativesLoading] = useState(false);
  const [alternativesError, setAlternativesError] = useState<string | null>(null);
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [dogs, setDogs] = useState("0");
  const [arrivalType, setArrivalType] = useState("motorhome");
  const [vehicleForm, setVehicleForm] = useState<VehicleFormData>({ ...EMPTY_VEHICLE_FORM });
  const [primaryGuestForm, setPrimaryGuestForm] = useState<PrimaryGuestFormData>(
    emptyPrimaryGuestForm,
  );
  const [primaryBirthDateBlurred, setPrimaryBirthDateBlurred] = useState(false);
  const [highlightMissingFields, setHighlightMissingFields] = useState(false);
  const primaryBirthEntryErrorRef = useRef<string | null>(null);
  const companionBirthEntryErrorRef = useRef<string | null>(null);
  const reportPrimaryBirthEntryError = useCallback((message: string | null) => {
    primaryBirthEntryErrorRef.current = message;
  }, []);
  const reportCompanionBirthEntryError = useCallback((message: string | null) => {
    companionBirthEntryErrorRef.current = message;
  }, []);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [bookingMessage, setBookingMessage] = useState("");
  const [companions, setCompanions] = useState(() =>
    expectedCompanionRoles(2, 0).map((role) => emptyCompanion(role)),
  );
  const [bookingConfig, setBookingConfig] = useState<PublicBookingConfig | null>(null);
  const [bookingConfigLoading, setBookingConfigLoading] = useState(true);
  const [bookingConfigError, setBookingConfigError] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(() => new Set());
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(() => new Set());
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BookingResult | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [guestLoggedIn, setGuestLoggedIn] = useState(false);
  const [knownCompanions, setKnownCompanions] = useState<KnownCompanion[]>([]);
  const [periodSelectedHint, setPeriodSelectedHint] = useState<string | null>(null);
  const [acceptedAlternative, setAcceptedAlternative] = useState<{
    arrival: string;
    departure: string;
    availableCount: number;
  } | null>(null);
  const skipCategoryResetRef = useRef(false);
  const skipPeriodResetRef = useRef(false);
  const pendingAdvanceAfterAvailabilityRef = useRef(false);
  const lastCheckedVehicleLengthRef = useRef<number | null | undefined>(undefined);
  const guestProfileTouchedRef = useRef(false);
  const [primaryFieldsToComplete, setPrimaryFieldsToComplete] = useState<
    import("@/lib/companionFields").CompanionFieldKey[]
  >([]);
  const [holdExpiresAt, setHoldExpiresAt] = useState<string | null>(null);
  const holdTokenRef = useRef<string | null>(null);
  const categoryPricesFetchKeyRef = useRef<string | null>(null);
  const categoriesFetchedWithPricesRef = useRef(false);
  const unfilteredCategoryCountRef = useRef(0);
  const categoryManuallySelectedRef = useRef(false);
  const quoteRequestIdRef = useRef(0);
  const [availabilityRequested, setAvailabilityRequested] = useState(false);
  const [periodCategories, setPeriodCategories] = useState<CategoriesResponse | null>(null);
  const [periodCategoriesLoading, setPeriodCategoriesLoading] = useState(false);
  const [backendPeriodBlock, setBackendPeriodBlock] = useState<string | null>(null);
  const [arrivalTypeCategories, setArrivalTypeCategories] = useState<CategoriesResponse | null>(
    null,
  );
  const [arrivalTypeCategoriesLoading, setArrivalTypeCategoriesLoading] = useState(false);
  const [debouncedVehicleLengthM, setDebouncedVehicleLengthM] = useState<number | null>(null);
  const [debouncedVehicleForm, setDebouncedVehicleForm] = useState<VehicleFormData>(EMPTY_VEHICLE_FORM);

  const releaseActiveHold = useCallback(async () => {
    const token = holdTokenRef.current;
    holdTokenRef.current = null;
    setHoldExpiresAt(null);
    if (!token) return;
    try {
      await api.delete(`/api/v1/public/booking/holds/${token}`);
    } catch {
      // Hold may already be expired or consumed
    }
  }, []);

  const clearCategorySelection = useCallback(() => {
    categoryManuallySelectedRef.current = false;
    setCategoryId(null);
    setQuote(null);
    quoteRequestIdRef.current += 1;
  }, []);

  const handleSelectCategory = useCallback((rawId: string) => {
    const id = normalizeCategoryId(rawId);
    if (!id) return;
    categoryManuallySelectedRef.current = true;
    setCategoryId(id);
    setQuote(null);
    quoteRequestIdRef.current += 1;
  }, []);

  const handleHoldExpired = useCallback(() => {
    void releaseActiveHold();
    clearCategorySelection();
    navigateToStep(3);
    setError(messages.flow.holdExpired);
  }, [clearCategorySelection, releaseActiveHold, messages.flow.holdExpired]);

  function applyGuestProfile(g: GuestProfile) {
    if (guestProfileTouchedRef.current) return;
    setPrimaryGuestForm((prev) => ({
      ...prev,
      first_name: g.first_name ?? "",
      last_name: g.last_name ?? "",
      email: g.email ?? "",
      phone: g.phone ?? "",
      street: g.street ?? "",
      postal_code: g.postal_code ?? "",
      city: g.city ?? "",
      country_code: g.country_code ?? "DE",
      license_plate: formatLicensePlateForDisplay(g.license_plate ?? ""),
    }));
  }

  useEffect(() => {
    if (!bookingConfig || wantsElectricityInitializedRef.current) return;
    wantsElectricityInitializedRef.current = true;
    setWantsElectricity(bookingConfig.flow.default_wants_electricity ?? true);
  }, [bookingConfig]);

  useEffect(() => {
    if (bookingConfigLoading) return;
    if (bookingConfig?.tourist_integration_enabled === true) {
      setGuestLoggedIn(false);
      setKnownCompanions([]);
      return;
    }
    const session = loadGuestSession();
    if (!session) return;
    setGuestLoggedIn(true);
    applyGuestProfile(session.guest);
    let cancelled = false;
    void (async () => {
      try {
        const me = await api.get<GuestProfile>("/api/v1/public/guest/me");
        if (cancelled) return;
        applyGuestProfile(me);
        saveGuestSession({ ...session, guest: me });
      } catch {
        // Profil aus der gespeicherten Sitzung weiterverwenden
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingConfigLoading, bookingConfig?.tourist_integration_enabled]);

  useEffect(() => {
    if (!guestLoggedIn || !loadGuestSession()) {
      setKnownCompanions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<{ companions: KnownCompanion[] }>(
          "/api/v1/public/guest/known-companions",
        );
        if (!cancelled) setKnownCompanions(data.companions ?? []);
      } catch {
        if (!cancelled) setKnownCompanions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [guestLoggedIn]);

  function normalizeCategoriesResponse(data: CategoriesResponse): CategoriesResponse {
    const periodBlockedMessage = data.period_blocked_message?.trim() || null;
    if (!periodBlockedMessage) return data;
    return {
      ...data,
      categories: [],
      period_blocked_message: periodBlockedMessage,
    };
  }

  function applyPeriodBlockFromCategories(
    data: CategoriesResponse,
    clearWhenAbsent = false,
  ): void {
    const periodBlockedMessage = data.period_blocked_message?.trim() || null;
    if (periodBlockedMessage) {
      setBackendPeriodBlock(periodBlockedMessage);
      return;
    }
    if (clearWhenAbsent) {
      setBackendPeriodBlock(null);
    }
  }

  function markGuestProfileTouched() {
    guestProfileTouchedRef.current = true;
  }

  function goToStep(target: Step) {
    setError(null);
    navigateToStep(target);
  }

  const companionFieldConfigs: CompanionFieldsConfigByRole = useMemo(
    () =>
      bookingConfig
        ? resolveCompanionFieldConfigs({
            companion_fields: normalizeGuestAddressFieldModes(
              bookingConfig.companion_fields ?? DEFAULT_COMPANION_FIELDS,
            ),
            adult: normalizeGuestAddressFieldModes(
              bookingConfig.companion_fields_adult ?? DEFAULT_COMPANION_FIELDS,
            ),
            child: normalizeGuestAddressFieldModes(
              bookingConfig.companion_fields_child ?? DEFAULT_COMPANION_FIELDS,
            ),
          })
        : resolveCompanionFieldConfigs({}),
    [bookingConfig],
  );

  const primaryGuestFieldConfig: CompanionFieldsConfig = useMemo(
    () =>
      normalizeGuestAddressFieldModes(
        bookingConfig?.primary_guest_fields ?? DEFAULT_PRIMARY_GUEST_FIELDS,
      ),
    [bookingConfig],
  );

  const arrivalTypeOptions = useMemo(
    () => bookingConfig?.arrival_types ?? [],
    [bookingConfig],
  );

  const flowSettings: OnlineBookingFlowSettings = bookingConfig?.flow ?? {
    show_electricity_option: true,
    default_wants_electricity: true,
    show_dogs_field: true,
    show_children_field: true,
    show_companion_details: true,
    require_guest_address: false,
    show_traveler_age_field: false,
  };

  const birthDateValidationEnabled = useMemo(
    () => birthDateAgeValidationActive(primaryGuestFieldConfig, companionFieldConfigs),
    [primaryGuestFieldConfig, companionFieldConfigs],
  );

  const primaryBirthDateError = useMemo(
    () =>
      birthDateValidationEnabled
        ? getPrimaryGuestBirthDateError({
            birthDate: primaryGuestForm.date_of_birth,
            arrivalDate: arrival,
            dobMode: primaryGuestFieldConfig.date_of_birth,
            messages,
          })
        : null,
    [
      birthDateValidationEnabled,
      primaryGuestForm.date_of_birth,
      arrival,
      primaryGuestFieldConfig.date_of_birth,
      messages,
    ],
  );

  const handleCompanionsChange = useCallback((nextCompanions: CompanionFormData[]) => {
    setCompanions(nextCompanions);
  }, []);

  const applyCompanionBirthDateAdjustments = useCallback(
    (nextCompanions: CompanionFormData[]) => {
      if (!birthDateValidationEnabled || !arrival.trim()) {
        setCompanions(nextCompanions);
        return;
      }
      const adjustment = applyBirthDatePartyAdjustments({
        arrivalDate: arrival,
        adults: Number(adults) || 0,
        children: Number(children) || 0,
        companions: nextCompanions,
        companionFieldConfigs,
      });
      setAdults(String(adjustment.adults));
      setChildren(String(adjustment.children));
      setCompanions(adjustment.companions);
    },
    [birthDateValidationEnabled, arrival, adults, children, companionFieldConfigs],
  );

  const addonArticles = bookingConfig?.addon_articles ?? [];
  const pitchFeatureOptions = bookingConfig?.pitch_features ?? [];
  const upsellingSettings: UpsellingDisplaySettings =
    bookingConfig?.upselling ?? DEFAULT_UPSELLING_DISPLAY;
  const bookingSeasons = bookingConfig?.booking_seasons ?? [];
  const minStaySettings = bookingConfig?.min_stay;
  const sameDayCutoffEnabled = bookingConfig?.same_day_cutoff?.enabled === true;
  const soldOutDates = useMemo(() => soldOutNightsSet(bookingConfig), [bookingConfig]);
  const touristIntegrationEnabled = bookingConfig?.tourist_integration_enabled === true;
  const availabilityCheckEnabled =
    touristIntegrationEnabled
      ? false
      : bookingConfig?.online_booking_availability_check !== false;

  const addonSelectionsPayload = useMemo(
    () =>
      addonArticles
        .filter((a) => selectedAddonIds.has(a.article_id))
        .map((a) => ({ article_id: a.article_id, quantity: 1 })),
    [addonArticles, selectedAddonIds],
  );

  const requestedFeaturePayload = useMemo(
    () => pitchFeatureOptions.filter((f) => selectedFeatureIds.has(f.id)).map((f) => f.id),
    [pitchFeatureOptions, selectedFeatureIds],
  );

  const selectedFeatureNames = useMemo(
    () => pitchFeatureOptions.filter((f) => selectedFeatureIds.has(f.id)).map((f) => f.name),
    [pitchFeatureOptions, selectedFeatureIds],
  );

  const vehicleFieldConfig = useMemo(
    () => vehicleFieldFullConfigFor(arrivalType, arrivalTypeOptions),
    [arrivalType, arrivalTypeOptions],
  );

  const vehicleLengthM = useMemo(
    () => vehicleLengthMFromForm(vehicleForm),
    [vehicleForm],
  );

  useEffect(() => {
    if (step < 2) {
      setDebouncedVehicleLengthM(null);
      setDebouncedVehicleForm(EMPTY_VEHICLE_FORM);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedVehicleLengthM(vehicleLengthM);
      setDebouncedVehicleForm(vehicleForm);
    }, 200);
    return () => clearTimeout(timer);
  }, [step, vehicleLengthM, vehicleForm]);

  const vehicleLengthFieldActive = activeVehicleFields(vehicleFieldConfig).includes(
    "vehicle_length_m",
  );

  useEffect(() => {
    const lengthCheckStep = touristIntegrationEnabled ? 3 : 2;
    if (step !== lengthCheckStep || !vehicleLengthFieldActive) {
      return;
    }
    if (lastCheckedVehicleLengthRef.current === debouncedVehicleLengthM) {
      return;
    }
    if (touristIntegrationEnabled) {
      if (!categoryManuallySelectedRef.current) {
        clearCategorySelection();
      }
    } else {
      setAvailabilityRequested(false);
    }
  }, [
    step,
    vehicleLengthFieldActive,
    debouncedVehicleLengthM,
    touristIntegrationEnabled,
    clearCategorySelection,
  ]);

  const vehicleStepReady = useMemo(
    () => vehicleFormReady(vehicleForm, vehicleFieldConfig),
    [vehicleForm, vehicleFieldConfig],
  );

  const vehicleSummary = useMemo(
    () => vehicleSummaryLines(vehicleForm, vehicleFieldConfig),
    [vehicleForm, vehicleFieldConfig],
  );

  useEffect(() => {
    if (step !== 5 || !guestLoggedIn) {
      setPrimaryFieldsToComplete((prev) => (prev.length === 0 ? prev : []));
      guestProfileTouchedRef.current = false;
      return;
    }
    setPrimaryFieldsToComplete((prev) => {
      if (prev.length > 0) return prev;
      return getMissingPrimaryGuestFields(primaryGuestForm, primaryGuestFieldConfig);
    });
  }, [step, guestLoggedIn, primaryGuestForm, primaryGuestFieldConfig]);

  const missingMandatoryFields = useMemo(
    () =>
      guestLoggedIn
        ? collectUnfilledMandatoryFields(
            primaryGuestForm,
            primaryGuestFieldConfig,
            companions,
            companionFieldConfigs,
            messages,
          )
        : [],
    [
      guestLoggedIn,
      primaryGuestForm,
      primaryGuestFieldConfig,
      companions,
      companionFieldConfigs,
      messages,
    ],
  );

  const mainGuestSummary = useMemo(
    () => primaryGuestSummaryLines(primaryGuestForm, primaryGuestFieldConfig, messages),
    [primaryGuestForm, primaryGuestFieldConfig, messages],
  );

  const clientPeriodError = validatePeriod(
    arrival,
    departure,
    bookingSeasons,
    minStaySettings,
    bookingConfig,
    messages,
    locale,
  );
  const backendPeriodBlockMessage = useMemo(
    () =>
      resolveBackendPeriodBlockMessage({
        backendPeriodBlock,
        periodCategoriesBlock: periodCategories?.period_blocked_message,
        arrivalTypeCategoriesBlock: arrivalTypeCategories?.period_blocked_message,
        categoriesBlock: categories?.period_blocked_message,
      }),
    [
      backendPeriodBlock,
      periodCategories?.period_blocked_message,
      arrivalTypeCategories?.period_blocked_message,
      categories?.period_blocked_message,
    ],
  );
  const periodError = clientPeriodError ?? backendPeriodBlockMessage;
  const showPeriodError = shouldShowPeriodValidationError(arrival, departure, periodError);
  const periodValid = Boolean(arrival && departure && !periodError);
  const periodDatesSelected = Boolean(arrival && departure && departure > arrival);
  const needsBackendPeriodVerification =
    periodDatesSelected &&
    (soldOutDates.size > 0 || touristIntegrationEnabled);
  const shouldPrefetchPeriod = needsBackendPeriodVerification && !clientPeriodError;
  const periodVerificationPending = shouldPrefetchPeriod && periodCategoriesLoading;
  const hasPeriodSoldOutBlock = useMemo(
    () =>
      hasSoldOutPeriodBlock(
        arrival,
        departure,
        bookingConfig,
        backendPeriodBlock,
        backendPeriodBlockMessage,
      ),
    [arrival, departure, bookingConfig, backendPeriodBlock, backendPeriodBlockMessage],
  );
  const vehicleFilterParams = useMemo(() => {
    if (step < 2) return {};
    if (touristIntegrationEnabled && step < 3) return {};
    return vehicleFilterParamsFromForm(debouncedVehicleForm, vehicleFieldConfig);
  }, [step, debouncedVehicleForm, vehicleFieldConfig, touristIntegrationEnabled]);
  const shouldVerifyArrivalType =
    availabilityCheckEnabled && periodValid && !hasPeriodSoldOutBlock && step >= 2;
  const shouldFetchCategories =
    periodValid &&
    !hasPeriodSoldOutBlock &&
    vehicleStepReady &&
    (availabilityRequested || !availabilityCheckEnabled);
  const vehicleDimensionsFilterAvailability = vehicleDimensionsAffectAvailability(
    vehicleFieldConfig,
  );

  useEffect(() => {
    if (!touristIntegrationEnabled || step < 3 || !vehicleDimensionsFilterAvailability) {
      return;
    }
    if (Object.keys(vehicleFilterParams).length === 0) {
      return;
    }
    setCategories(null);
    if (!categoryManuallySelectedRef.current) {
      clearCategorySelection();
    }
  }, [
    step,
    touristIntegrationEnabled,
    vehicleDimensionsFilterAvailability,
    vehicleFilterParams,
    clearCategorySelection,
  ]);

  const arrivalTypeHasAvailability = (arrivalTypeCategories?.categories.length ?? 0) > 0;
  const arrivalTypeAvailabilityChecked =
    shouldVerifyArrivalType &&
    !arrivalTypeCategoriesLoading &&
    arrivalTypeCategories !== null;
  const arrivalTypeUnavailable =
    availabilityCheckEnabled &&
    arrivalTypeAvailabilityChecked &&
    !arrivalTypeHasAvailability;
  const nights = arrival && departure ? nightsBetween(arrival, departure) : 0;
  const arrivalMin = useMemo(
    () => earliestBookableArrivalDate(bookingConfig),
    [bookingConfig],
  );
  const departureMin = arrival ? addDays(arrival, 1) : addDays(arrivalMin, 1);
  const isDateDisabledForCalendar = useCallback(
    (iso: string, field: "arrival" | "departure") => {
      if (
        field === "arrival" &&
        iso === todayLocal() &&
        isSameDayArrivalBlockedToday(bookingConfig)
      ) {
        return true;
      }
      if (field === "arrival" && isArrivalBlockedBySoldOut(iso, soldOutDates)) {
        return true;
      }
      if (field === "departure" && isDepartureBlockedBySoldOut(iso, arrival, soldOutDates)) {
        return true;
      }
      if (bookingSeasons.length === 0) return false;
      if (field === "arrival") {
        return !isDateBookable(iso, bookingSeasons);
      }
      if (!arrival) return false;
      return !isBookingPeriodInSeasons(arrival, iso, bookingSeasons);
    },
    [bookingConfig, bookingSeasons, arrival, soldOutDates],
  );
  const selectedCategory = useMemo(() => {
    if (!categories || categoryId === null) return null;
    return categories.categories.find(
      (c) => categoryIdKey(c.pitch_category_id) === categoryIdKey(categoryId),
    );
  }, [categories, categoryId]);

  const hasBookableCategory = (categories?.categories.length ?? 0) > 0;
  const acceptedAlternativeMatchesPeriod =
    acceptedAlternative !== null &&
    acceptedAlternative.arrival === arrival &&
    acceptedAlternative.departure === departure &&
    acceptedAlternative.availableCount > 0;
  const hasResolvedPeriodAvailability =
    hasBookableCategory || acceptedAlternativeMatchesPeriod;
  const showStepCategories = Boolean(
    categories &&
      categories.categories.length > 0 &&
      (availabilityRequested || !availabilityCheckEnabled) &&
      !hasPeriodSoldOutBlock,
  );
  const needsSoldOutPeriodNotice =
    hasPeriodSoldOutBlock && showPeriodError && Boolean(periodError);
  const needsVehiclePitchNotice =
    touristIntegrationEnabled &&
    step === 3 &&
    vehicleDimensionsFilterAvailability &&
    vehicleStepReady &&
    !categoriesLoading &&
    categories !== null &&
    categories.categories.length === 0 &&
    unfilteredCategoryCountRef.current > 0 &&
    periodValid &&
    !hasPeriodSoldOutBlock;
  const needsAlternativePeriods =
    availabilityCheckEnabled &&
    availabilityRequested &&
    step === 2 &&
    vehicleStepReady &&
    periodValid &&
    !hasPeriodSoldOutBlock &&
    !categoriesLoading &&
    categories !== null &&
    !hasResolvedPeriodAvailability;
  const hasResolvedCategoryAvailability =
    !availabilityCheckEnabled ||
    (selectedCategory?.available_count ?? 0) > 0 ||
    acceptedAlternativeMatchesPeriod;
  const needsCategoryAlternatives =
    availabilityCheckEnabled &&
    periodValid &&
    !hasPeriodSoldOutBlock &&
    !categoriesLoading &&
    categories !== null &&
    hasBookableCategory &&
    step === 3 &&
    categoryId !== null &&
    !hasResolvedCategoryAvailability;
  const shouldFetchAlternatives = needsAlternativePeriods || needsCategoryAlternatives;
  const canCheckAvailabilityStep2 =
    vehicleStepReady &&
    periodValid &&
    !hasPeriodSoldOutBlock &&
    !categoriesLoading &&
    !arrivalTypeCategoriesLoading;
  const noCategoryForArrival =
    !availabilityCheckEnabled &&
    vehicleStepReady &&
    periodValid &&
    !hasPeriodSoldOutBlock &&
    !categoriesLoading &&
    categories !== null &&
    categories.categories.length === 0;
  const canContinueStep3 =
    (availabilityCheckEnabled ? availabilityRequested : vehicleStepReady) &&
    !categoriesLoading &&
    categories !== null &&
    (!availabilityCheckEnabled || !needsCategoryAlternatives) &&
    categoryId !== null &&
    hasResolvedCategoryAvailability;
  const showAvailabilityLoading =
    availabilityCheckEnabled &&
    categoriesLoading &&
    availabilityRequested &&
    ((step === 2 && vehicleStepReady) || step === 3);
  const [showAvailabilityOverlay, setShowAvailabilityOverlay] = useState(false);

  useEffect(() => {
    if (!showAvailabilityLoading) {
      setShowAvailabilityOverlay(false);
      return;
    }
    const timer = window.setTimeout(() => setShowAvailabilityOverlay(true), 250);
    return () => clearTimeout(timer);
  }, [showAvailabilityLoading]);

  useEffect(() => {
    let cancelled = false;
    setBookingConfigLoading(true);
    setBookingConfigError(null);
    api
      .get<PublicBookingConfig>("/api/v1/public/booking/config", { locale })
      .then((cfg) => {
        if (!cancelled) {
          setBookingConfig(cfg);
          setBookingConfigError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setBookingConfig(null);
          setBookingConfigError(
            err instanceof Error
              ? err.message
              : messages.flow.configLoadError,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setBookingConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale, messages.flow.configLoadError]);

  useEffect(() => {
    if (!hasPeriodSoldOutBlock || step < 2 || step >= 7) {
      return;
    }
    navigateToStep(1);
    setAvailabilityRequested(false);
    setCategories(null);
  }, [hasPeriodSoldOutBlock, step]);

  useEffect(() => {
    if (arrivalTypeOptions.length === 0) return;
    if (!arrivalTypeOptions.some((t) => t.value === arrivalType)) {
      setArrivalType(arrivalTypeOptions[0].value);
      clearCategorySelection();
    }
  }, [arrivalTypeOptions, arrivalType, clearCategorySelection]);

  useEffect(() => {
    setCompanions((prev) =>
      syncCompanionsToPartyCounts(prev, Number(adults) || 0, Number(children) || 0),
    );
  }, [adults, children]);

  useEffect(() => {
    if (!birthDateValidationEnabled || !arrival.trim()) return;
    const adjustment = applyBirthDatePartyAdjustments({
      arrivalDate: arrival,
      adults: Number(adults) || 0,
      children: Number(children) || 0,
      companions,
      companionFieldConfigs,
    });
    const adultsCount = Number(adults) || 0;
    const childrenCount = Number(children) || 0;
    const partyChanged =
      adjustment.adults !== adultsCount ||
      adjustment.children !== childrenCount ||
      adjustment.companions.some((companion, index) => companion.role !== companions[index]?.role);
    if (!partyChanged) return;
    setAdults(String(adjustment.adults));
    setChildren(String(adjustment.children));
    setCompanions(adjustment.companions);
  }, [arrival, birthDateValidationEnabled, companionFieldConfigs]);

  const loadQuote = useCallback(async () => {
    const pitchCategoryId = pitchCategoryIdForApi(categoryId);
    if (pitchCategoryId === null) return;
    const requestId = ++quoteRequestIdRef.current;
    setQuoteLoading(true);
    try {
      const body = {
        pitch_category_id: pitchCategoryId,
        arrival_date: arrival,
        departure_date: departure,
        adults: Number(adults),
        children: Number(children),
        dogs: Number(dogs) || 0,
        wants_electricity: wantsElectricity,
        addon_selections: addonSelectionsPayload,
      };
      const data = await api.post<PriceQuote>("/api/v1/public/booking/quote", body);
      if (requestId !== quoteRequestIdRef.current) return;
      setQuote(data);
    } catch (e) {
      if (requestId !== quoteRequestIdRef.current) return;
      setQuote(null);
      setError(e instanceof ApiError ? e.message : messages.flow.priceCalcError);
    } finally {
      if (requestId === quoteRequestIdRef.current) {
        setQuoteLoading(false);
      }
    }
  }, [arrival, departure, categoryId, adults, children, dogs, wantsElectricity, addonSelectionsPayload, messages.flow.priceCalcError]);

  useEffect(() => {
    setCategories(null);
    setQuote(null);
    setAlternatives(null);
    setAlternativesError(null);
    setPeriodCategories(null);
    setBackendPeriodBlock(null);
    setArrivalTypeCategories(null);
    if (!skipPeriodResetRef.current) {
      setPeriodSelectedHint(null);
      setAcceptedAlternative(null);
      setAvailabilityRequested(false);
      lastCheckedVehicleLengthRef.current = undefined;
    }
    skipPeriodResetRef.current = false;
    if (!skipCategoryResetRef.current) {
      clearCategorySelection();
    }
    skipCategoryResetRef.current = false;
  }, [arrival, departure, wantsElectricity, clearCategorySelection]);

  const availabilityRequestedRef = useRef(availabilityRequested);
  availabilityRequestedRef.current = availabilityRequested;

  useEffect(() => {
    if (!skipCategoryResetRef.current) {
      clearCategorySelection();
    }
    skipCategoryResetRef.current = false;
    setArrivalTypeCategories(null);
    if (availabilityRequestedRef.current) {
      setCategories(null);
      setAvailabilityRequested(false);
      lastCheckedVehicleLengthRef.current = undefined;
    }
  }, [arrivalType, clearCategorySelection]);

  useEffect(() => {
    void releaseActiveHold();
  }, [arrival, departure, wantsElectricity, arrivalType, vehicleForm, releaseActiveHold]);

  useEffect(() => {
    const pitchCategoryId = pitchCategoryIdForApi(categoryId);
    if (!availabilityCheckEnabled || step < 3 || step >= 7 || !pitchCategoryId || !periodValid) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.post<BookingHold>("/api/v1/public/booking/holds", {
          pitch_category_id: pitchCategoryId,
          arrival_date: arrival,
          departure_date: departure,
          wants_electricity: wantsElectricity,
          arrival_type: arrivalType,
          vehicle_length_m: vehicleLengthM,
          vehicle_width_m: vehicleFilterParams.vehicle_width_m
            ? Number(vehicleFilterParams.vehicle_width_m)
            : null,
          vehicle_height_m: vehicleFilterParams.vehicle_height_m
            ? Number(vehicleFilterParams.vehicle_height_m)
            : null,
          vehicle_weight_kg: vehicleFilterParams.vehicle_weight_kg
            ? Number(vehicleFilterParams.vehicle_weight_kg)
            : null,
          hold_token: holdTokenRef.current,
        });
        if (cancelled) return;
        holdTokenRef.current = data.hold_token;
        setHoldExpiresAt(data.expires_at);
      } catch (e) {
        if (cancelled) return;
        await releaseActiveHold();
        clearCategorySelection();
        setError(
          e instanceof ApiError
            ? e.message
            : messages.flow.holdCreateError,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    availabilityCheckEnabled,
    step,
    categoryId,
    periodValid,
    arrival,
    departure,
    wantsElectricity,
    releaseActiveHold,
    clearCategorySelection,
    arrivalType,
    vehicleLengthM,
    vehicleFilterParams,
  ]);

  useEffect(() => {
    return () => {
      const token = holdTokenRef.current;
      if (token) {
        void api.delete(`/api/v1/public/booking/holds/${token}`).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (categoriesLoading || categories === null) {
      return;
    }
    if (hasBookableCategory || step > 1) {
      setError((current) => (current === messages.flow.staleAlternative ? null : current));
      return;
    }
    if (!acceptedAlternativeMatchesPeriod) {
      return;
    }
    setAcceptedAlternative(null);
    setPeriodSelectedHint(null);
    setError(messages.flow.staleAlternative);
  }, [
    categoriesLoading,
    categories,
    acceptedAlternativeMatchesPeriod,
    hasBookableCategory,
    step,
    messages.flow.staleAlternative,
  ]);

  useEffect(() => {
    if (!shouldPrefetchPeriod) {
      setPeriodCategories(null);
      return;
    }
    let cancelled = false;
    setPeriodCategoriesLoading(true);
    const params = buildCategoriesQueryParams({
      arrival,
      departure,
      wantsElectricity,
      holdToken: holdTokenRef.current,
      locale,
    });
    api
      .get<CategoriesResponse>(`/api/v1/public/booking/categories?${params}`)
      .then((data) => {
        if (!cancelled) {
          const normalized = normalizeCategoriesResponse(data);
          setPeriodCategories(normalized);
          applyPeriodBlockFromCategories(normalized, true);
        }
      })
      .catch(() => {
        if (!cancelled) setPeriodCategories(null);
      })
      .finally(() => {
        if (!cancelled) setPeriodCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldPrefetchPeriod, arrival, departure, wantsElectricity, locale]);

  useEffect(() => {
    if (!shouldVerifyArrivalType) {
      setArrivalTypeCategories(null);
      return;
    }
    let cancelled = false;
    setArrivalTypeCategoriesLoading(true);
    const params = buildCategoriesQueryParams({
      arrival,
      departure,
      wantsElectricity,
      arrivalType,
      holdToken: holdTokenRef.current,
      locale,
    });
    api
      .get<CategoriesResponse>(`/api/v1/public/booking/categories?${params}`)
      .then((data) => {
        if (!cancelled) {
          const normalized = normalizeCategoriesResponse(data);
          setArrivalTypeCategories(normalized);
          applyPeriodBlockFromCategories(normalized);
        }
      })
      .catch(() => {
        if (!cancelled) setArrivalTypeCategories(null);
      })
      .finally(() => {
        if (!cancelled) setArrivalTypeCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldVerifyArrivalType, arrival, departure, wantsElectricity, arrivalType, locale]);

  useEffect(() => {
    if (!shouldFetchCategories) {
      return;
    }
    let cancelled = false;
    categoryPricesFetchKeyRef.current = null;
    categoriesFetchedWithPricesRef.current = false;
    setCategoriesLoading(true);
    const params = buildCategoriesQueryParams({
      arrival,
      departure,
      wantsElectricity,
      arrivalType,
      vehicleFilterParams,
      holdToken: holdTokenRef.current,
      locale,
      includePrices: true,
    });
    api
      .get<CategoriesResponse>(`/api/v1/public/booking/categories?${params}`)
      .then((data) => {
        if (!cancelled) {
          const normalized = normalizeCategoriesResponse(data);
          if (touristIntegrationEnabled && Object.keys(vehicleFilterParams).length === 0) {
            unfilteredCategoryCountRef.current = normalized.categories.length;
          }
          setCategories(normalized);
          categoriesFetchedWithPricesRef.current = true;
          applyPeriodBlockFromCategories(normalized);
          setCategoryId((current) => {
            const preserveManual = categoryManuallySelectedRef.current;
            const next = resolveCategorySelectionAfterFetch(
              normalized.categories,
              current,
              normalized.suggested_category_id,
              preserveManual,
            );
            if (
              preserveManual &&
              current &&
              categoryIdKey(next) !== categoryIdKey(current)
            ) {
              categoryManuallySelectedRef.current = false;
            }
            return next;
          });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setCategories(null);
          setError(
            e instanceof ApiError ? e.message : messages.flow.availabilityLoadError,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    shouldFetchCategories,
    arrival,
    departure,
    wantsElectricity,
    vehicleFilterParams,
    arrivalType,
    touristIntegrationEnabled,
  ]);

  useEffect(() => {
    if (categoriesLoading || !categories?.categories.length) {
      return;
    }
    if (categoriesFetchedWithPricesRef.current) {
      categoriesFetchedWithPricesRef.current = false;
      return;
    }
    const categoryIds = categories.categories
      .map((cat) => cat.pitch_category_id ?? "none")
      .join(",");
    const fetchKey = `${arrival}|${departure}|${wantsElectricity}|${categoryIds}`;
    if (categoryPricesFetchKeyRef.current === fetchKey) {
      return;
    }
    categoryPricesFetchKeyRef.current = fetchKey;
    let cancelled = false;
    const params = new URLSearchParams({
      arrival,
      departure,
      wants_electricity: String(wantsElectricity),
      category_ids: categoryIds,
      locale,
    });
    api
      .get<CategoryPricesResponse>(`/api/v1/public/booking/category-prices?${params}`)
      .then((data) => {
        if (!cancelled) {
          setCategories((current) => (current ? mergeCategoryPrices(current, data) : current));
        }
      })
      .catch(() => {
        if (!cancelled) {
          categoryPricesFetchKeyRef.current = null;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    categoriesLoading,
    categories,
    arrival,
    departure,
    wantsElectricity,
  ]);

  useEffect(() => {
    if (!shouldFetchAlternatives) {
      setAlternatives(null);
      setAlternativesError(null);
      return;
    }
    const params = new URLSearchParams({
      arrival,
      departure,
      wants_electricity: String(wantsElectricity),
    });
    appendVehicleAvailabilityParams(params, vehicleFilterParams, arrivalType);
    if (needsCategoryAlternatives && categoryId) {
      params.set("category_id", categoryId);
    }
    let cancelled = false;
    setAlternativesLoading(true);
    setAlternativesError(null);
    api
      .get<AlternativePeriodsResponse>(
        `/api/v1/public/booking/alternatives?${params.toString()}`,
      )
      .then((data) => {
        if (!cancelled) {
          setAlternatives(data);
          setAlternativesError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setAlternatives(null);
          setAlternativesError(
            err instanceof ApiError
              ? err.message
              : messages.flow.alternativesLoadError,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAlternativesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    shouldFetchAlternatives,
    needsCategoryAlternatives,
    categoryId,
    arrival,
    departure,
    wantsElectricity,
    vehicleFilterParams,
    arrivalType,
  ]);

  useEffect(() => {
    if (step >= 4 && categoryId !== undefined && arrival && departure) {
      void loadQuote();
    }
  }, [step, categoryId, arrival, departure, adults, children, wantsElectricity, loadQuote]);

  function applyAlternativePeriod(alt: AlternativePeriod, options?: { keepCategory?: boolean }) {
    skipPeriodResetRef.current = true;
    skipCategoryResetRef.current = Boolean(options?.keepCategory);
    setAcceptedAlternative({
      arrival: alt.arrival,
      departure: alt.departure,
      availableCount: alt.available_count,
    });
    setArrival(alt.arrival);
    setDeparture(alt.departure);
    if (!options?.keepCategory) {
      clearCategorySelection();
    }
    setError(null);
    const places = messages.category.available(alt.available_count);
    setPeriodSelectedHint(
      `${formatLocaleDate(alt.arrival, locale)} – ${formatLocaleDate(alt.departure, locale)}: ${places}`,
    );
  }

  function clearAcceptedAlternativePeriod() {
    setAcceptedAlternative(null);
    setPeriodSelectedHint(null);
  }

  function handleArrivalChange(value: string) {
    clearAcceptedAlternativePeriod();
    setArrival(value);
    // Abreise bleibt leer, bis sie ausdrücklich gewählt wird. Ein vorausgefüllter
    // 3-Tage-Zeitraum wurde als feste Buchungsdauer verstanden.
    if (departure && (!value || departure <= value)) {
      setDeparture("");
    }
  }

  function handleDepartureChange(value: string) {
    clearAcceptedAlternativePeriod();
    setDeparture(value);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    if (periodError) {
      setError(periodError);
      setSubmitting(false);
      return;
    }
    if (!periodValid) {
      setSubmitting(false);
      return;
    }
    const vehicleError = validateVehicleForm(vehicleForm, vehicleFieldConfig);
    if (vehicleError) {
      setError(vehicleError);
      setSubmitting(false);
      return;
    }
    if (availabilityCheckEnabled && !holdTokenRef.current) {
      setError(messages.flow.holdExpired);
      setSubmitting(false);
      return;
    }
    try {
      let data: BookingResult;
      const holdPayload = holdTokenRef.current
        ? { hold_token: holdTokenRef.current }
        : {};
      const basePrimaryPayload = buildPrimaryGuestPayload(primaryGuestForm, primaryGuestFieldConfig);
      const partyAdjustment = birthDateAgeValidationActive(
        primaryGuestFieldConfig,
        companionFieldConfigs,
      )
        ? applyBirthDatePartyAdjustments({
            arrivalDate: arrival,
            adults: Number(adults),
            children: Number(children),
            companions,
            companionFieldConfigs,
          })
        : {
            adults: Number(adults),
            children: Number(children),
            companions,
          };
      const baseCompanions = flowSettings.show_companion_details
        ? buildCompanionsPayload(partyAdjustment.companions, companionFieldConfigs)
        : partyAdjustment.companions.map((companion) => ({ role: companion.role }));
      const requestBody = {
        locale,
        idempotency_key: idempotencyKey,
        pitch_category_id: pitchCategoryIdForApi(categoryId),
        arrival_date: arrival,
        departure_date: departure,
        adults: partyAdjustment.adults,
        children: partyAdjustment.children,
        dogs: Number(dogs),
        arrival_type: arrivalType,
        wants_electricity: wantsElectricity,
        requested_feature_ids: requestedFeaturePayload,
        ...holdPayload,
        ...basePrimaryPayload,
        ...buildVehiclePayload(vehicleForm, vehicleFieldConfig),
        companions: baseCompanions,
        addon_selections: addonSelectionsPayload,
        accept_terms: acceptTerms,
        notes: bookingMessage.trim() || null,
      };
      if (guestLoggedIn && !touristIntegrationEnabled && loadGuestSession()) {
        data = await api.post<BookingResult>("/api/v1/public/guest/booking/requests", requestBody);
      } else {
        data = await api.post<BookingResult>("/api/v1/public/booking/requests", requestBody);
      }
      setResult(data);
      holdTokenRef.current = null;
      setHoldExpiresAt(null);
      setStepDirection("forward");
      setStep(7);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : messages.flow.submitError);
    } finally {
      setSubmitting(false);
    }
  }

  function handleCheckAvailability() {
    setError(null);
    if (hasPeriodSoldOutBlock || periodError) {
      if (periodError) setError(periodError);
      return;
    }
    if (!periodValid) {
      return;
    }
    const vehicleError = validateVehicleForm(vehicleForm, vehicleFieldConfig);
    if (vehicleError) {
      setError(vehicleError);
      return;
    }
    if (vehicleLengthFieldActive) {
      setDebouncedVehicleLengthM(vehicleLengthM);
      lastCheckedVehicleLengthRef.current = vehicleLengthM;
    }
    if (arrivalTypeUnavailable && arrivalTypeCategories) {
      setCategories(arrivalTypeCategories);
      clearCategorySelection();
      setAvailabilityRequested(true);
      return;
    }
    if (
      !vehicleDimensionsFilterAvailability &&
      arrivalTypeAvailabilityChecked &&
      arrivalTypeCategories
    ) {
      setCategories(arrivalTypeCategories);
      setCategoryId((current) =>
        resolveCategorySelectionAfterFetch(
          arrivalTypeCategories.categories,
          current,
          arrivalTypeCategories.suggested_category_id,
          categoryManuallySelectedRef.current,
        ),
      );
      setAvailabilityRequested(true);
      return;
    }
    setAvailabilityRequested(true);
  }

  function handleCheckAvailabilityFromFooter() {
    if (categoriesLoading) {
      return;
    }
    if (!availabilityCheckEnabled) {
      setError(null);
      if (hasPeriodSoldOutBlock || periodError) {
        if (periodError) setError(periodError);
        return;
      }
      if (!periodValid || periodVerificationPending) {
        return;
      }
      const vehicleError = validateVehicleForm(vehicleForm, vehicleFieldConfig);
      if (vehicleError) {
        setError(vehicleError);
        return;
      }
      if (!vehicleStepReady) {
        return;
      }
      if (categories !== null && categories.categories.length > 0) {
        setAvailabilityRequested(true);
        navigateToStep(3);
        return;
      }
      if (categories !== null && categories.categories.length === 0) {
        setError(messages.flow.noPitchForArrival);
        return;
      }
      pendingAdvanceAfterAvailabilityRef.current = true;
      setAvailabilityRequested(true);
      return;
    }
    if (
      availabilityRequested &&
      categories !== null &&
      !needsAlternativePeriods &&
      hasResolvedPeriodAvailability
    ) {
      navigateToStep(3);
      return;
    }
    pendingAdvanceAfterAvailabilityRef.current = true;
    handleCheckAvailability();
  }

  useEffect(() => {
    if (!pendingAdvanceAfterAvailabilityRef.current) {
      return;
    }
    if (step !== 2 || !availabilityRequested || categoriesLoading || categories === null) {
      return;
    }
    pendingAdvanceAfterAvailabilityRef.current = false;
    if (hasResolvedPeriodAvailability) {
      setError(null);
      navigateToStep(3);
      return;
    }
    if (!availabilityCheckEnabled) {
      setError(messages.flow.noPitchForArrival);
    }
  }, [
    step,
    availabilityRequested,
    categoriesLoading,
    categories,
    hasResolvedPeriodAvailability,
    availabilityCheckEnabled,
    messages.flow.noPitchForArrival,
  ]);

  function handleTryWithoutElectricity() {
    skipPeriodResetRef.current = true;
    setWantsElectricity(false);
    setAvailabilityRequested(true);
  }

  function goNext() {
    setError(null);
    if (step === 1) {
      if (hasPeriodSoldOutBlock || periodError) {
        if (periodError) setError(periodError);
        return;
      }
      if (!periodValid || periodVerificationPending) {
        return;
      }
      navigateToStep(2);
      return;
    }
    if (step === 2) {
      handleCheckAvailabilityFromFooter();
      return;
    }
    if (step === 3) {
      if (availabilityCheckEnabled && !availabilityRequested) {
        setError(messages.flow.checkAvailability);
        return;
      }
      if (categoriesLoading) {
        return;
      }
      if (categoryId === null && categories?.categories.length) {
        setError(messages.flow.selectCategory);
        return;
      }
      if (!categories?.categories.length) {
        setError(
          needsVehiclePitchNotice
            ? messages.availability.noVehiclePitchTitle
            : availabilityCheckEnabled
              ? messages.flow.noPitchWithSuggestions
              : periodError || messages.flow.noPitch,
        );
        return;
      }
      if (availabilityCheckEnabled && !hasResolvedCategoryAvailability) {
        setError(messages.flow.selectAvailablePeriod);
        return;
      }
      navigateToStep(4);
      return;
    }
    if (step === 4) {
      navigateToStep(5);
      return;
    }
    if (step === 5) {
      const rejectContactStep = (message: string, blurBirthDate = false) => {
        if (blurBirthDate) setPrimaryBirthDateBlurred(true);
        setHighlightMissingFields(true);
        setError(message);
        window.setTimeout(() => {
          document
            .querySelector<HTMLElement>("[data-missing-field='true']")
            ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }, 60);
      };
      const birthEntryError =
        primaryBirthEntryErrorRef.current || companionBirthEntryErrorRef.current;
      if (birthEntryError) {
        rejectContactStep(birthEntryError, true);
        return;
      }
      const primaryGuestError = validatePrimaryGuest(
        primaryGuestForm,
        primaryGuestFieldConfig,
        messages,
      );
      if (primaryGuestError) {
        rejectContactStep(primaryGuestError);
        return;
      }
      if (flowSettings.show_companion_details) {
        const companionError = validateCompanions(companions, companionFieldConfigs, messages);
        if (companionError) {
          rejectContactStep(companionError);
          return;
        }
      }
      if (birthDateAgeValidationActive(primaryGuestFieldConfig, companionFieldConfigs)) {
        const birthDateError = validateTravelerBirthDates({
          arrivalDate: arrival,
          primaryGuestForm,
          primaryGuestFieldConfig,
          companions,
          companionFieldConfigs,
          messages,
        });
        if (birthDateError) {
          rejectContactStep(birthDateError, true);
          return;
        }
      }
      if (!acceptTerms) {
        rejectContactStep(messages.flow.acceptTerms);
        return;
      }
      navigateToStep(6);
      return;
    }
    if (step === 6) {
      if (availabilityCheckEnabled && !holdTokenRef.current) {
        setError(messages.flow.holdExpired);
        return;
      }
      void handleSubmit();
    }
  }

  function goBack() {
    setError(null);
    if (step > 1 && step < 7) {
      setStepDirection("back");
      setStep((s) => (s - 1) as Step);
    }
  }

  const showPriceInFooter = step >= 4 && step <= 6;
  const nextButtonDisabled =
    submitting ||
    (step === 1 && (!periodValid || bookingConfigLoading || periodVerificationPending)) ||
    (step === 2 &&
      (bookingConfigLoading ||
        arrivalTypeOptions.length === 0 ||
        !canCheckAvailabilityStep2 ||
        !periodValid ||
        (needsAlternativePeriods && availabilityRequested))) ||
    (step === 3 &&
      (!canContinueStep3 || (alternativesLoading && needsCategoryAlternatives)));
  const siteName = bookingConfig?.site_name?.trim() || fallbackSiteName;
  const logoUrl = bookingConfig?.logo_url ?? fallbackLogoUrl;
  const legalContext = useMemo(
    () => bookingLegalContextFromConfig(bookingConfig?.online_booking_legal, siteName),
    [bookingConfig?.online_booking_legal, siteName],
  );

  if (step === 7 && result) {
    return (
      <BookingSuccessScreen
        siteName={siteName}
        logoUrl={logoUrl}
        title={messages.flow.thankYouTitle}
        message={result.message}
        bookingNumber={result.booking.booking_number}
        bookingNumberLabel={messages.flow.bookingNumber}
        showBookingNumber={result.booking.status !== "requested"}
        arrival={result.booking.arrival_date}
        departure={result.booking.departure_date}
        arrivalLabel={messages.common.arrival}
        departureLabel={messages.common.departure}
        locale={locale}
        quote={result.price}
        touristIntegrationEnabled={touristIntegrationEnabled}
        guestLoggedIn={guestLoggedIn}
        portalLabel={messages.guestAuth.portal}
        portalPath={localePath("/portal")}
      />
    );
  }

  const nightsLabel = `${nights} ${nights === 1 ? messages.common.night : messages.common.nights}`;
  const periodLabel =
    arrival && departure
      ? `${formatLocaleDate(arrival, locale)} – ${formatLocaleDate(departure, locale)}`
      : "";
  const vehicleContextLabel = [
    arrivalTypeLabel(arrivalType, arrivalTypeOptions),
    ...vehicleSummary
      .filter((line) => line.key !== "license_plate")
      .map((line) => line.value),
  ]
    .filter(Boolean)
    .join(" · ");
  const guestsContextLabel = [
    `${adults} ${messages.common.adults}`,
    Number(children) > 0 ? `${children} ${messages.common.children}` : "",
    Number(dogs) > 0 ? `${dogs} ${messages.common.dogs}` : "",
  ]
    .filter(Boolean)
    .join(", ");
  const contextItems: StayContextItem[] = [
    {
      key: "period",
      icon: <CalendarIcon />,
      label: messages.flow.summaryPeriod,
      value: periodLabel,
    },
    { key: "nights", icon: <MoonIcon />, value: nights > 0 ? nightsLabel : "" },
    {
      key: "electricity",
      icon: <BoltIcon />,
      label: messages.electricity.legend,
      value: flowSettings.show_electricity_option
        ? wantsElectricity
          ? messages.electricity.withLabel
          : messages.electricity.withoutLabel
        : "",
    },
    {
      key: "vehicle",
      icon: <VehicleTypeIcon code={arrivalType} />,
      label: messages.arrivalType.legend,
      value: step >= 3 ? vehicleContextLabel : "",
    },
    {
      key: "category",
      icon: <PinIcon />,
      label: messages.flow.categoryLabel,
      value: step >= 4 && selectedCategory ? selectedCategory.name : "",
    },
    {
      key: "guests",
      icon: <UsersIcon />,
      label: messages.flow.travellersTitle,
      value: step >= 5 ? guestsContextLabel : "",
    },
  ];
  const totalSteps = flowSteps.length;

  return (
    <div className="bk-page min-h-screen py-8 pb-40">
      <BookingBrandHeader
        siteName={siteName}
        logoUrl={logoUrl}
        title={messages.flow.pageTitle}
        subtitle={messages.flow.pageSubtitle}
      />

      <BookingStepIndicator steps={flowSteps} current={step} />

      {holdExpiresAt && step >= 3 && step < 7 ? (
        <BookingHoldTimer
          expiresAt={holdExpiresAt}
          onExpired={handleHoldExpired}
          className="mb-4"
        />
      ) : null}

      <AnimatedAlert show={!!error} className="mb-4">
        <Notice variant="error" role="status">
          {error}
        </Notice>
      </AnimatedAlert>

      <AnimatePresence mode="wait" custom={stepDirection}>
      {step === 1 && (
        <BookingStepTransition stepKey={1} direction={stepDirection}>
        <section className="bk-card space-y-6">
          <StepHeader
            step={1}
            total={totalSteps}
            title={messages.stepHeadings.period.title}
            intro={messages.stepHeadings.period.intro}
          />
          <div className="space-y-3">
            <BookingPeriodDateFields
              arrivalId="booking-arrival"
              departureId="booking-departure"
              arrival={arrival}
              departure={departure}
              arrivalMin={arrivalMin}
              departureMin={departureMin}
              isDateDisabled={
                bookingSeasons.length > 0 || soldOutDates.size > 0 || sameDayCutoffEnabled
                  ? isDateDisabledForCalendar
                  : undefined
              }
              onArrivalChange={handleArrivalChange}
              onDepartureChange={handleDepartureChange}
            />
            {needsSoldOutPeriodNotice && periodError ? (
              <SoldOutPeriodNotice
                arrival={arrival}
                departure={departure}
                nights={nights}
                message={periodError}
              />
            ) : showPeriodError ? (
              <p className="text-sm font-medium text-red-700" role="alert">
                {periodError}
              </p>
            ) : periodVerificationPending ? (
              <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
                <Spinner />
                {availabilityCheckEnabled
                  ? messages.flow.checkingAvailability
                  : messages.common.loading}
              </p>
            ) : nights > 0 ? (
              <p className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800">
                <MoonIcon className="h-4 w-4" />
                {nightsLabel}
              </p>
            ) : null}
            {bookingSeasons.length > 0 || soldOutDates.size > 0 ? (
              <ul className="space-y-1 text-xs leading-relaxed text-slate-500">
                {bookingSeasons.length > 0 ? (
                  <li className="flex gap-2">
                    <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {messages.season.bookableOnly}
                      {bookingSeasons.map((s) => s.name).filter(Boolean).length > 0
                        ? ` (${bookingSeasons.map((s) => s.name).filter(Boolean).join(", ")})`
                        : ""}
                      . {messages.season.closingDayDepartureOnly}
                    </span>
                  </li>
                ) : null}
                {soldOutDates.size > 0 ? (
                  <li className="flex gap-2">
                    <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{messages.flow.soldOutDaysHint}</span>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>
          {flowSettings.show_electricity_option ? (
            <>
              <hr className="bk-divider" />
              <ElectricitySelection
                value={wantsElectricity}
                onChange={setWantsElectricity}
              />
            </>
          ) : null}
          {!touristIntegrationEnabled ? (
            <>
              <hr className="bk-divider" />
              <GuestAuthPrompt guestLoggedIn={guestLoggedIn} />
            </>
          ) : null}
        </section>
        </BookingStepTransition>
      )}

      {step === 2 && (
        <BookingStepTransition stepKey={2} direction={stepDirection}>
        <section className="bk-card space-y-6">
          <StepHeader
            step={2}
            total={totalSteps}
            title={messages.stepHeadings.vehicle.title}
            intro={messages.stepHeadings.vehicle.intro}
          />
          <StayContextBar items={contextItems} />
          {showPeriodError && periodError && !needsSoldOutPeriodNotice ? (
            <Notice variant="error">{periodError}</Notice>
          ) : null}
          {needsSoldOutPeriodNotice && periodError ? (
            <SoldOutPeriodNotice
              arrival={arrival}
              departure={departure}
              nights={nights}
              message={periodError}
            />
          ) : null}
          {bookingConfigLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Spinner />
              {messages.common.loading}
            </p>
          ) : bookingConfigError ? (
            <Notice variant="error">{bookingConfigError}</Notice>
          ) : (
            <ArrivalTypeSelection
              options={arrivalTypeOptions}
              value={arrivalType}
              disabled={arrivalTypeOptions.length === 0}
              onChange={(code) => {
                setArrivalType(code);
                clearCategorySelection();
              }}
            />
          )}
          {!bookingConfigLoading && !bookingConfigError && arrivalTypeOptions.length > 0 ? (
            <>
              <hr className="bk-divider" />
              <VehicleDetailsFields
                form={vehicleForm}
                config={vehicleFieldConfig}
                onChange={(next) => {
                  setVehicleForm(next);
                  clearCategorySelection();
                }}
              />
            </>
          ) : null}
          {!vehicleStepReady && activeVehicleFields(vehicleFieldConfig).length > 0 ? (
            <p className="text-xs text-slate-500">{messages.flow.missingFieldsHint}</p>
          ) : null}
          {noCategoryForArrival ? (
            <Notice variant="warning">{messages.flow.noPitchForArrival}</Notice>
          ) : null}
          {periodValid &&
          !hasPeriodSoldOutBlock &&
          !periodCategoriesLoading &&
          periodCategories &&
          periodCategories.categories.length === 0 &&
          !noCategoryForArrival ? (
            <Notice variant="warning">{periodError || messages.flow.noPitch}</Notice>
          ) : null}
          {arrivalTypeUnavailable && vehicleStepReady && !availabilityRequested ? (
            <Notice variant="warning">
              {messages.flow.arrivalTypeUnavailable(
                arrivalTypeLabel(arrivalType, arrivalTypeOptions),
                formatLocaleDate(arrival, locale),
                formatLocaleDate(departure, locale),
              )}
            </Notice>
          ) : null}
          {arrivalTypeCategoriesLoading && step === 2 && !availabilityRequested ? (
            <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
              <Spinner />
              {messages.flow.checkingAvailability}
            </p>
          ) : null}
          {periodSelectedHint &&
          (acceptedAlternativeMatchesPeriod || !needsAlternativePeriods) ? (
            <PeriodSelectedHint message={periodSelectedHint} />
          ) : null}
          {availabilityRequested &&
          vehicleStepReady &&
          !categoriesLoading &&
          categories &&
          hasResolvedPeriodAvailability &&
          !hasPeriodSoldOutBlock ? (
            <Notice variant="success">{messages.flow.pitchAvailableHint}</Notice>
          ) : null}
          {vehicleStepReady && needsAlternativePeriods ? (
            <>
              <NoAvailabilityNotice
                arrival={arrival}
                departure={departure}
                nights={nights}
                wantsElectricity={wantsElectricity}
                onTryWithoutElectricity={wantsElectricity ? handleTryWithoutElectricity : undefined}
              />
              <AlternativePeriodSuggestions
                show
                loading={alternativesLoading}
                alternatives={alternatives}
                error={alternativesError}
                onSelect={applyAlternativePeriod}
              />
            </>
          ) : null}
        </section>
        </BookingStepTransition>
      )}

      {step === 3 && (
        <BookingStepTransition stepKey={3} direction={stepDirection}>
        <section className="bk-card space-y-6">
          <StepHeader
            step={3}
            total={totalSteps}
            title={messages.stepHeadings.pitch.title}
            intro={messages.stepHeadings.pitch.intro}
          />
          <StayContextBar items={contextItems} />
          {showPeriodError && periodError && !needsSoldOutPeriodNotice ? (
            <Notice variant="error">{periodError}</Notice>
          ) : null}
          {needsSoldOutPeriodNotice && periodError ? (
            <SoldOutPeriodNotice
              arrival={arrival}
              departure={departure}
              nights={nights}
              message={periodError}
            />
          ) : null}
          {periodSelectedHint &&
          (acceptedAlternativeMatchesPeriod || !needsAlternativePeriods) ? (
            <PeriodSelectedHint message={periodSelectedHint} />
          ) : null}
          {touristIntegrationEnabled && categoriesLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
              <Spinner />
              {messages.flow.checkingAvailability}
            </p>
          ) : null}
          {showStepCategories ? (
            <CategorySelectionCards
              categories={categories!.categories}
              selectedId={categoryId}
              onSelect={handleSelectCategory}
              upselling={upsellingSettings}
              showAvailabilityCount={availabilityCheckEnabled}
            />
          ) : null}
          {needsVehiclePitchNotice ? (
            <NoVehiclePitchNotice
              vehicleLabel={arrivalTypeLabel(arrivalType, arrivalTypeOptions)}
            />
          ) : null}
          {!availabilityCheckEnabled &&
          !hasPeriodSoldOutBlock &&
          !categoriesLoading &&
          categories &&
          categories.categories.length === 0 &&
          !needsVehiclePitchNotice ? (
            <Notice variant="warning">{periodError || messages.flow.noPitch}</Notice>
          ) : null}
          {needsCategoryAlternatives ? (
            <AlternativePeriodSuggestions
              show
              loading={alternativesLoading}
              alternatives={alternatives}
              error={alternativesError}
              categoryLabel={selectedCategory?.name}
              onSelect={(alt) => applyAlternativePeriod(alt, { keepCategory: true })}
            />
          ) : null}
        </section>
        </BookingStepTransition>
      )}

      {step === 4 && (
        <BookingStepTransition stepKey={4} direction={stepDirection}>
        <section className="bk-card space-y-6">
          <StepHeader
            step={4}
            total={totalSteps}
            title={messages.stepHeadings.details.title}
            intro={messages.stepHeadings.details.intro}
          />
          <StayContextBar items={contextItems} />
          <section className="bk-section">
            <h3 className="bk-section-title">{messages.flow.travellersTitle}</h3>
            <div
              className={clsx(
                "grid grid-cols-1 gap-4 rounded-xl border border-slate-200 p-4",
                flowSettings.show_children_field && flowSettings.show_dogs_field
                  ? "sm:grid-cols-3"
                  : flowSettings.show_children_field || flowSettings.show_dogs_field
                    ? "sm:grid-cols-2"
                    : "",
              )}
            >
              <PartyCountField
                label={messages.common.adults}
                hint={messages.common.adultsAgeHint}
                value={adults}
                onChange={setAdults}
                minCount={1}
              />
              {flowSettings.show_children_field ? (
                <PartyCountField
                  label={messages.common.children}
                  hint={messages.common.childrenAgeHint}
                  value={children}
                  onChange={setChildren}
                />
              ) : null}
              {flowSettings.show_dogs_field ? (
                <PartyCountField label={messages.common.dogs} value={dogs} onChange={setDogs} />
              ) : null}
            </div>
          </section>
          {pitchFeatureOptions.length > 0 ? (
            <>
              <hr className="bk-divider" />
              <OptionTileGroup
                title={messages.flow.featuresTitle}
                hint={messages.flow.featuresHint}
                items={pitchFeatureOptions.map((feature) => ({
                  id: feature.id,
                  title: feature.name,
                  description: feature.description,
                }))}
                selectedIds={selectedFeatureIds}
                onToggle={(id, selected) => {
                  setSelectedFeatureIds((prev) => {
                    const next = new Set(prev);
                    if (selected) next.add(id);
                    else next.delete(id);
                    return next;
                  });
                }}
              />
            </>
          ) : null}
          {addonArticles.length > 0 ? (
            <>
              <hr className="bk-divider" />
              <OptionTileGroup
                title={messages.flow.addonsTitle}
                items={addonArticles.map((addon) => ({
                  id: addon.article_id,
                  title: addon.name,
                  description: addon.description,
                  meta: addon.unit_price
                    ? `${messages.category.from} ${formatCurrency(addon.unit_price, "EUR", locale)}`
                    : null,
                }))}
                selectedIds={selectedAddonIds}
                onToggle={(id, selected) => {
                  setSelectedAddonIds((prev) => {
                    const next = new Set(prev);
                    if (selected) next.add(id);
                    else next.delete(id);
                    return next;
                  });
                }}
              />
            </>
          ) : null}
          <PriceBreakdown quote={quote} loading={quoteLoading} />
        </section>
        </BookingStepTransition>
      )}

      {step === 5 && (
        <BookingStepTransition stepKey={5} direction={stepDirection}>
        <section className="bk-card space-y-6">
          <StepHeader
            step={5}
            total={totalSteps}
            title={guestLoggedIn ? messages.flow.savedData : messages.stepHeadings.contact.title}
            intro={messages.stepHeadings.contact.intro}
          />
          <StayContextBar items={contextItems} />
          {guestLoggedIn ? (
            <div className="space-y-5">
              {missingMandatoryFields.length > 0 ? (
                <Notice variant="warning" title={messages.flow.missingFieldsHint}>
                  <ul className="list-disc space-y-0.5 pl-5">
                    {missingMandatoryFields.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </Notice>
              ) : null}
              {primaryFieldsToComplete.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="bk-section-title">{messages.fields.main_guest}</p>
                  <SummaryRows rows={mainGuestSummary} />
                  <p className="mt-3 text-xs text-slate-500">{messages.flow.savedProfileHint}</p>
                </div>
              ) : (
                <PrimaryGuestFieldsForm
                  form={primaryGuestForm}
                  config={primaryGuestFieldConfig}
                  googlePlacesApiKey={bookingConfig?.google_places_api_key}
                  birthDateError={primaryBirthDateError}
                  showBirthDateError={primaryBirthDateBlurred}
                  onBirthDateBlur={() => setPrimaryBirthDateBlurred(true)}
                  onBirthDateEntryError={reportPrimaryBirthEntryError}
                  highlightMissing={highlightMissingFields}
                  onChange={(next) => {
                    markGuestProfileTouched();
                    setPrimaryGuestForm(next);
                  }}
                  description={messages.flow.missingFieldsHint}
                />
              )}
              {flowSettings.show_companion_details ? (
                <CompanionFieldsForm
                  companions={companions}
                  configs={companionFieldConfigs}
                  onChange={handleCompanionsChange}
                  onBirthDateBlur={applyCompanionBirthDateAdjustments}
                  onBirthDateEntryError={reportCompanionBirthEntryError}
                  knownCompanions={knownCompanions}
                  googlePlacesApiKey={bookingConfig?.google_places_api_key}
                  highlightMissing={highlightMissingFields}
                />
              ) : null}
            </div>
          ) : null}
          {!guestLoggedIn ? (
            <div className="space-y-5">
              <PrimaryGuestFieldsForm
                form={primaryGuestForm}
                config={primaryGuestFieldConfig}
                googlePlacesApiKey={bookingConfig?.google_places_api_key}
                birthDateError={primaryBirthDateError}
                showBirthDateError={primaryBirthDateBlurred}
                onBirthDateBlur={() => setPrimaryBirthDateBlurred(true)}
                onBirthDateEntryError={reportPrimaryBirthEntryError}
                highlightMissing={highlightMissingFields}
                onChange={(next) => { setPrimaryGuestForm(next); markGuestProfileTouched(); }}
              />
              {flowSettings.show_companion_details ? (
                <CompanionFieldsForm
                  companions={companions}
                  configs={companionFieldConfigs}
                  onChange={handleCompanionsChange}
                  onBirthDateBlur={applyCompanionBirthDateAdjustments}
                  onBirthDateEntryError={reportCompanionBirthEntryError}
                  googlePlacesApiKey={bookingConfig?.google_places_api_key}
                  highlightMissing={highlightMissingFields}
                />
              ) : null}
            </div>
          ) : null}
          <hr className="bk-divider" />
          <label className="bk-label">
            {messages.flow.messageLabel}
            <span className="bk-label__optional">({messages.flow.optionalLabel})</span>
            <textarea
              className="bk-input mt-1 min-h-[5rem] w-full resize-y"
              value={bookingMessage}
              onChange={(e) => setBookingMessage(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder={messages.flow.messagePlaceholder}
            />
          </label>
          <BookingTermsAcceptance
            checked={acceptTerms}
            onChange={setAcceptTerms}
            locale={locale}
            legalContext={legalContext}
            termsPrefix={messages.flow.termsPrefix}
            termsAnd={messages.flow.termsAnd}
            termsSuffix={messages.flow.termsSuffix}
            termsLink={messages.flow.termsLink}
            privacyLink={messages.flow.privacyLink}
            closeLabel={messages.flow.legalClose}
            missing={highlightMissingFields}
          />
        </section>
        </BookingStepTransition>
      )}

      {step === 6 && (
        <BookingStepTransition stepKey={6} direction={stepDirection}>
        <section className="bk-card space-y-6">
          <StepHeader
            step={6}
            total={totalSteps}
            title={messages.stepHeadings.summary.title}
            intro={messages.stepHeadings.summary.intro}
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <SummarySection
              title={messages.flow.summaryPeriod}
              icon={<CalendarIcon />}
              editLabel={messages.common.edit}
              onEdit={() => goToStep(1)}
            >
              <p className="text-sm font-semibold text-slate-900">{periodLabel}</p>
              <p className="mt-0.5 text-sm text-slate-600">
                {nightsLabel}
                {flowSettings.show_electricity_option
                  ? ` · ${wantsElectricity ? messages.electricity.withLabel : messages.electricity.withoutLabel}`
                  : ""}
              </p>
            </SummarySection>

            <SummarySection
              title={messages.flow.summaryVehicle}
              icon={<PinIcon />}
              editLabel={messages.common.edit}
              onEdit={() => goToStep(3)}
            >
              <p className="text-sm font-semibold text-slate-900">
                {selectedCategory?.name ?? messages.fields.pitch}
              </p>
              <p className="mt-0.5 text-sm text-slate-600">
                {arrivalTypeLabel(arrivalType, arrivalTypeOptions)}
                {vehicleSummary.length > 0
                  ? vehicleSummary.map((line) => ` · ${line.label}: ${line.value}`).join("")
                  : ""}
              </p>
            </SummarySection>

            <SummarySection
              title={messages.flow.summaryParty}
              icon={<UsersIcon />}
              editLabel={messages.common.edit}
              onEdit={() => goToStep(4)}
            >
              <p className="text-sm font-semibold text-slate-900">{guestsContextLabel}</p>
              {selectedFeatureNames.length > 0 ? (
                <p className="mt-0.5 text-sm text-slate-600">
                  {messages.flow.featuresTitle}: {selectedFeatureNames.join(", ")}
                </p>
              ) : null}
              {addonSelectionsPayload.length > 0 ? (
                <p className="mt-0.5 text-sm text-slate-600">
                  {messages.flow.addonsTitle}:{" "}
                  {addonArticles
                    .filter((a) => selectedAddonIds.has(a.article_id))
                    .map((a) => a.name)
                    .join(", ")}
                </p>
              ) : null}
            </SummarySection>

            <SummarySection
              title={messages.flow.summaryContact}
              icon={<MessageIcon />}
              editLabel={messages.common.edit}
              onEdit={() => goToStep(5)}
            >
              {mainGuestSummary.length > 0 ? (
                <SummaryRows rows={mainGuestSummary} />
              ) : (
                <p className="text-sm font-medium text-slate-900">{messages.common.dash}</p>
              )}
            </SummarySection>

            {companions.length > 0 && flowSettings.show_companion_details ? (
              <div className="sm:col-span-2">
                <SummarySection
                  title={messages.flow.companionsSummary}
                  icon={<UsersIcon />}
                  editLabel={messages.common.edit}
                  onEdit={() => goToStep(5)}
                >
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(() => {
                      let adultIndex = 0;
                      let childIndex = 0;
                      return companions.map((companion, index) => {
                        const slotIndex =
                          companion.role === "child" ? childIndex++ : adultIndex++;
                        const title = companionSlotLabel(companion.role, slotIndex, messages);
                        const lines = companionSummaryLines(
                          companion,
                          companionFieldConfigs,
                          messages,
                        );
                        return (
                          <div
                            key={`${companion.role}-${index}`}
                            className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm"
                          >
                            <p className="font-semibold text-slate-800">{title}</p>
                            {lines.length > 0 ? (
                              <SummaryRows
                                rows={lines.map(({ key, label, value }) => ({
                                  label,
                                  value: formatCompanionFieldValue(key, value),
                                }))}
                              />
                            ) : (
                              <p className="mt-1 text-slate-500">
                                {messages.companionFields.no_details}
                              </p>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </SummarySection>
              </div>
            ) : null}

            {bookingMessage.trim() ? (
              <div className="sm:col-span-2">
                <SummarySection
                  title={messages.flow.summaryMessage}
                  icon={<MessageIcon />}
                  editLabel={messages.common.edit}
                  onEdit={() => goToStep(5)}
                >
                  <p className="whitespace-pre-wrap text-sm text-slate-700">
                    {bookingMessage.trim()}
                  </p>
                </SummarySection>
              </div>
            ) : null}
          </div>

          <PriceBreakdown quote={quote} loading={quoteLoading} />
        </section>
        </BookingStepTransition>
      )}
      </AnimatePresence>

      <AnimatePresence>
      {showAvailabilityOverlay ? <AvailabilityCheckingScreen key="avail-overlay" /> : null}
      </AnimatePresence>

      <footer className="bk-footer-bar fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/95 py-3 backdrop-blur">
        <div className="bk-page space-y-3">
          <div className="flex items-center gap-3">
            {showPriceInFooter && (quote || quoteLoading) ? (
              <PriceBreakdown quote={quote} loading={quoteLoading} compact className="min-w-0 flex-1" />
            ) : (
              <p className="hidden min-w-0 flex-1 text-xs text-slate-500 md:block">
                {messages.flow.stepOf(step, totalSteps)} · {flowSteps[step - 1]?.label}
              </p>
            )}
            <div className="flex flex-1 gap-3 md:flex-none md:justify-end">
              {step > 1 && (
                <button
                  type="button"
                  className="bk-btn-secondary flex-1 md:flex-none md:min-w-[8rem]"
                  onClick={goBack}
                >
                  {messages.flow.previous}
                </button>
              )}
              <button
                type="button"
                className={clsx(
                  "bk-btn-primary flex-1 md:flex-none md:min-w-[12rem]",
                  !nextButtonDisabled && "bk-btn-primary--pulse",
                )}
                onClick={step === 2 ? handleCheckAvailabilityFromFooter : goNext}
                disabled={nextButtonDisabled}
              >
                {step === 6
                  ? submitting
                    ? messages.flow.sending
                    : messages.flow.submitRequest
                  : step === 2
                    ? categoriesLoading ? (
                        <>
                          <Spinner className="mr-2" />
                          {availabilityCheckEnabled
                            ? messages.flow.checkingAvailability
                            : messages.flow.loadingCategories}
                        </>
                      ) : needsAlternativePeriods && availabilityRequested
                        ? messages.flow.selectPeriodFirst
                        : !availabilityCheckEnabled ||
                            (availabilityRequested && hasResolvedPeriodAvailability)
                          ? messages.flow.selectPitch
                          : messages.flow.checkAvailability
                    : step === 1 && periodVerificationPending
                      ? (
                          <>
                            <Spinner className="mr-2" />
                            {availabilityCheckEnabled
                              ? messages.flow.checkingAvailability
                              : messages.common.loading}
                          </>
                        )
                      : step === 3 && needsCategoryAlternatives
                      ? messages.flow.selectPeriodFirst
                      : messages.flow.next}
              </button>
            </div>
          </div>
          {step === 2 &&
          needsAlternativePeriods &&
          availabilityRequested &&
          !categoriesLoading &&
          !alternativesLoading ? (
            <p className="text-center text-xs text-slate-500">
              {messages.availability.choosePeriod}
            </p>
          ) : null}
        </div>
      </footer>
    </div>
  );
}
